<template>
    <responsive-button
        @click="exportToPDF"
        :icon="['far', 'file-pdf']"
        :responsiveness="responsiveness"
        :disabled="disabled"
        :variant="variant"
        :busy="busy"
    >
        <span v-if="displayLabel">{{ label }}</span>
    </responsive-button>
</template>

<script>
import { mapGetters } from 'vuex';
import { saveAs } from 'file-saver';
import API from '../../../api.jsx';
import ResponsiveButton from '../responsive-button/responsive-button.vue';


export default {
    components: { ResponsiveButton },
    props: {
        href: {
            type: String,
            default: null,
        },
        fileName: {
            type: String,
            default: 'file',
        },
        pageOrientation: {
            type: String,
            default: 'landscape',
        },
        limit: {
            type: Number,
            default: 30000,
        },
        tableFontSize: {
            type: String,
            default: 'md',
        },
        onTooManyRecords: {
            type: Function,
            default: null,
        },
        responsiveness: {
            type: Boolean,
            default: true,
        },
        displayLabel: {
            type: Boolean,
            default: false,
        },
        label: {
            type: String,
            default: 'Export to PDF',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        pdfCacheKey: {
            type: String,
            default: '',
        },
        variant: {
            type: String,
            default: 'primary'
        }
    },
    data() {
        return {
            busy: false,
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat']),
    },
    methods: {
        async exportToPDF() {
            if (
                this.href && this.pdfCacheKey
            ) {
                this.generatingPdfReportSuccessToast();
                this.busy = true;
                this.$emit('busy', true);
                try {
                    const query =
                        this.href.split('?').length > 1
                            ? this.href.split('?')[1]
                            : '';

                    const params = Object.fromEntries(
                        new URLSearchParams(query)
                    );
                    const response = await API.post(
                        this.href,
                        {
                            pdf_cache_key: this.pdfCacheKey,
                            default_orientation: this.pageOrientation,
                            table_font_size: this.tableFontSize,
                            ...params,
                        },
                        {
                            responseType: 'blob',
                        }
                    );
                    saveAs(
                        response.data,
                        this.getFilenameFromResponse(response)
                    );
                } catch (err) {
                    const text =
                        !!err.response && !!err.response.data
                            ? await err.response.data.text()
                            : null;
                    if (text && text.includes('Too many records')) {
                        if (this.onTooManyRecords) {
                            this.onTooManyRecords();
                        } else {
                            this.tooManyRecordsError();
                        }
                    } else {
                        this.errorToast({ text: "Couldn't download PDF." });
                    }
                } finally {
                    this.busy = false;
                    this.$emit('busy', false);
                }
                return;
            } else if (this.href) {
                this.generatingPdfReportSuccessToast();
                this.busy = true;
                try {
                    const response = await API.get(this.href, {
                        responseType: 'blob',
                    });
                    saveAs(
                        response.data,
                        this.getFilenameFromResponse(response)
                    );
                } catch (err) {
                    const text =
                        !!err.response && !!err.response.data
                            ? await err.response.data.text()
                            : null;
                    if (text && text.includes('Too many records')) {
                        if (this.onTooManyRecords) {
                            this.onTooManyRecords();
                        } else {
                            this.tooManyRecordsError();
                        }
                    } else {
                        this.errorToast({ text: "Couldn't download PDF." });
                    }
                } finally {
                    this.busy = false;
                }
            }
        },
        getFilenameFromResponse(response) {
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                return /filename="(?<filename>.*)"/.exec(contentDisposition)
                    ?.groups?.filename;
            }
        },
        tooManyRecordsError() {
            this.errorToast({
                text: "Couldn't generate PDF because of too many records.",
            });
        },
    },
};
</script>
